import { useEffect, useState } from 'react';
import {
  Card,
  Input,
  Button,
  Checkbox,
  Select,
  SelectItem,
  Selection,
  Pagination,
} from '@nextui-org/react';
import { Settlement } from 'common/src/utils/types';
import { getSettlementsBySuperAdmin } from 'common/src/api/settlements';
import { Icon } from '@iconify/react';
import EditSettlementDialog from '../../../components/settlements/edit-settlement-dialog';
import SettlementsTable from '../../../components/settlements/tables/settlements-table';
import DeleteSettlementModal from '../../../components/settlements/modals/delete-settlement-modal';
import RecalculateMarketModal from '../../../components/settlements/modals/recalculate-vs-market-modal';
import { toast } from 'react-toastify';
import { updateSettlement } from 'common/src/api/settlements';
import * as XLSX from 'xlsx';
import { format, addHours } from 'date-fns';
import {
  calculateWeightedAverage,
  getSettlementIncoterm,
} from 'common/src/utils/functions';
import { calculateFOBSettlement } from '../../../utils/functions';
import { supabase } from 'common/src/supabase';
import Header from '../../../components/layouts/Header';
import { LoadingState, PageLayout } from '../../../components/ui';

const ROWS_PER_PAGE = 10;

const prepareSettlementsData = (settlements: Settlement[]) => {
  return settlements.map((settlement) => ({
    Referencia: settlement.reference_number,
    Exportador: settlement.exporter?.name || 'Sin Exportador',
    Cliente: settlement.receiver,
    Nave: settlement.ship_trip?.ship?.name || 'Sin embarque',
    'Fecha Venta': format(new Date(settlement.date_sold), 'dd/MM/yyyy'),
    'Kilos Totales': settlement.settlement_details.reduce(
      (sum, detail) => sum + (detail.kilos || 0),
      0
    ),
    Estado: settlement.upload_status,
    Incoterm:
      getSettlementIncoterm(settlement) === 'pre_liquidacion'
        ? 'Pre Liquidación'
        : getSettlementIncoterm(settlement).toUpperCase(),
    'Incoterm Original': settlement.original_incoterm?.toUpperCase() || 'N/A',
    'Liquidación FOB': calculateFOBSettlement(settlement),
    'Diferencia vs Mercado':
      calculateWeightedAverage(
        settlement.settlement_details.map((detail) => ({
          kilos: detail.kilos,
          value: detail.difference_vs_market_eta,
        })),
        (item) => item.value
      ).toFixed(2) + '%',
  }));
};

const prepareSettlementDetailsData = (settlements: Settlement[]) => {
  const details: Array<Record<string, string | number | null>> = [];
  settlements.forEach((settlement) => {
    settlement.settlement_details.forEach((detail) => {
      details.push({
        Referencia: settlement.reference_number,
        Exportador: settlement.exporter?.name || 'Sin Exportador',
        Cliente: settlement.receiver,
        Nave: settlement.ship_trip?.ship?.name || 'Sin embarque',
        'ETD Nave': settlement.ship_trip?.etd
          ? format(
              addHours(new Date(settlement.ship_trip.etd.split('T')[0]), 10),
              'dd/MM/yyyy'
            )
          : 'N/A',
        'ETA Nave': settlement.ship_trip?.eta
          ? format(
              addHours(new Date(settlement.ship_trip.eta.split('T')[0]), 10),
              'dd/MM/yyyy'
            )
          : 'N/A',
        POD: settlement.ship_trip?.market?.name || 'Sin POD',
        'Fecha Venta': format(new Date(settlement.date_sold), 'dd/MM/yyyy'),
        Estado: settlement.upload_status,
        'Incoterm Original':
          settlement.original_incoterm?.toUpperCase() || 'N/A',
        Contenedor: settlement.container_number,
        'Mercado Venta': settlement.market?.name || 'Sin Mercado',
        Pallet: detail.pallet,
        Variedad: detail.variety,
        Bultos: detail.quantity,
        Calibre: detail.caliber,
        Color: detail.color,
        Formato: detail.format,
        'Peso Unitario': detail.unit_weight,
        Etiqueta: detail.label,
        Kilos: detail.kilos,
        'Moneda Local': detail.local_currency,
        'Venta Moneda Local': detail.local_total_price,
        'Comisión Moneda Local': detail.local_total_commision,
        'Impuestos Moneda Local': detail.local_total_tax,
        'Otros Costos Moneda Local': detail.local_other_costs,
        'Repacking Moneda Local': detail.local_repacking_costs,
        'CIF Moneda Local':
          detail.local_total_price -
          detail.local_total_tax -
          detail.local_total_commision -
          detail.local_other_costs -
          (detail.local_repacking_costs || 0),
        'Flete Moneda Local': detail.usd_freight * detail.exchange_rate,
        'FOB Moneda Local': detail.usd_total_fob * detail.exchange_rate,
        'Tipo de Cambio': detail.exchange_rate,
        'Venta USD': detail.local_total_price / detail.exchange_rate,
        'Comisión USD': detail.local_total_commision / detail.exchange_rate,
        'Impuestos USD': detail.local_total_tax / detail.exchange_rate,
        'Otros Costos USD': detail.local_other_costs / detail.exchange_rate,
        'Repacking USD':
          (detail.local_repacking_costs || 0) / detail.exchange_rate,
        'CIF USD':
          (detail.local_total_price -
            detail.local_total_tax -
            detail.local_total_commision -
            detail.local_other_costs -
            (detail.local_repacking_costs || 0)) /
          detail.exchange_rate,
        'Flete USD': detail.usd_freight,
        'FOB USD': detail.usd_total_fob,
        'Diferencia vs Mercado ETA %': detail.difference_vs_market_eta
          ? `${detail.difference_vs_market_eta.toFixed(2)}%`
          : 'N/A',
      });
    });
  });
  return details;
};

const SuperadminSettlements = () => {
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState<string>('');
  const [selectedSettlement, setSelectedSettlement] =
    useState<Settlement | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [notifyViaEmail, setNotifyViaEmail] = useState(false);
  const [settlementToDelete, setSettlementToDelete] =
    useState<Settlement | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isRecalculateModalOpen, setIsRecalculateModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [processingSettlement, setProcessingSettlement] = useState(false);

  const fetchSettlements = async () => {
    setLoading(true);
    const start = (page - 1) * rowsPerPage;
    const { data, count } = await getSettlementsBySuperAdmin({
      start,
      limit: rowsPerPage,
      searchTerm,
      season: selectedSeason,
      status: showPendingOnly ? 'pending' : 'all',
      includeDetails: true,
    });

    // Ensure the data is a valid Settlement array
    if (Array.isArray(data)) {
      setSettlements(data as Settlement[]);
    } else {
      setSettlements([]);
    }

    setTotalCount(count || 0);
    setLoading(false);
  };

  const fetchAllSettlementsForExport = async () => {
    try {
      // First get count of all settlements to determine batch size
      const { count } = await getSettlementsBySuperAdmin({
        searchTerm,
        season: selectedSeason,
        status: showPendingOnly ? 'pending' : 'all',
        includeDetails: false,
      });

      const BATCH_SIZE = 1000;
      const totalBatches = Math.ceil((count || 0) / BATCH_SIZE);
      let allSettlements: Settlement[] = [];

      // Show loading message for large datasets
      if (totalBatches > 1) {
        toast.info(
          `Cargando ${count} liquidaciones en ${totalBatches} lotes. Esto puede tomar un momento...`
        );
      }

      // Fetch data in batches to avoid timeouts and memory issues
      for (let batch = 0; batch < totalBatches; batch++) {
        const start = batch * BATCH_SIZE;
        const { data } = await getSettlementsBySuperAdmin({
          start,
          limit: BATCH_SIZE,
          searchTerm,
          season: selectedSeason,
          status: showPendingOnly ? 'pending' : 'all',
          includeDetails: true,
        });

        // Validate data and add to all settlements
        if (Array.isArray(data) && data.length > 0) {
          allSettlements = [...allSettlements, ...(data as Settlement[])];
        }

        if (totalBatches > 1 && batch < totalBatches - 1) {
          toast.info(`Cargado lote ${batch + 1} de ${totalBatches}`);
        }
      }

      return allSettlements;
    } catch (error) {
      console.error('Error fetching all settlements:', error);
      toast.error('Error al obtener las liquidaciones');
      return [];
    }
  };

  useEffect(() => {
    fetchSettlements();
  }, [page, rowsPerPage, searchTerm, showPendingOnly, selectedSeason]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, showPendingOnly, selectedSeason]);

  const uniqueSeasons = [...new Set(settlements.map((s) => s.season))]
    .sort()
    .reverse();

  const handleEditSettlement = (settlement: Settlement) => {
    setSelectedSettlement(settlement);
    setIsEditDialogOpen(true);
  };

  const handleDeleteSettlement = (settlement: Settlement) => {
    setSettlementToDelete(settlement);
    setIsDeleteDialogOpen(true);
  };

  const handleApproveSettlement = async (settlement: Settlement) => {
    try {
      // Activar el loading a nivel de página
      setProcessingSettlement(true);

      const newStatus =
        settlement.upload_status === 'approved' ? 'pending' : 'approved';

      // Actualizar estado de la liquidación
      await updateSettlement(
        settlement.id,
        {
          upload_status: newStatus,
        },
        true,
        notifyViaEmail
      );

      // Si la liquidación fue aprobada, calcular datos de mercado
      if (newStatus === 'approved') {
        try {
          // Llamar a la nueva función RPC para calcular datos de mercado usando el ID específico
          const { data: marketResult, error: marketError } = await supabase.rpc(
            'update_settlements_market_data_by_ids',
            {
              settlement_ids: [settlement.id], // Array con un solo ID (el de la liquidación aprobada)
            }
          );

          if (marketError) {
            console.error('Error al calcular datos de mercado:', marketError);
            toast.warning(
              'Liquidación aprobada, pero hubo un error al calcular datos de mercado'
            );
          } else {
            console.log('Datos de mercado calculados:', marketResult);
            toast.success(
              `Liquidación aprobada correctamente y datos de mercado calculados (${marketResult.details_processed} detalles procesados)`
            );
          }
        } catch (marketError) {
          console.error('Error al calcular datos de mercado:', marketError);
          toast.warning(
            'Liquidación aprobada, pero hubo un error al calcular datos de mercado'
          );
        }
      } else {
        // Si se marca como pendiente, solo mostrar mensaje de éxito normal
        toast.success('Liquidación marcada como pendiente correctamente');
      }

      // Actualizar la lista de liquidaciones
      await fetchSettlements();
    } catch (error) {
      toast.error('Error al actualizar el estado de la liquidación');
      console.error('Error updating settlement status:', error);
    } finally {
      // Desactivar el loading cuando termine el proceso
      setProcessingSettlement(false);
    }
  };

  const exportAllSettlements = async () => {
    setDownloadingExcel(true);
    try {
      // Create workbook
      const wb = XLSX.utils.book_new();

      // Fetch all settlements with the optimized function
      const allSettlements = await fetchAllSettlementsForExport();

      if (allSettlements.length === 0) {
        toast.warning(
          'No hay liquidaciones para exportar con los filtros seleccionados'
        );
        setDownloadingExcel(false);
        return;
      }

      // Create details sheet
      const detailsData = prepareSettlementDetailsData(allSettlements);
      const wsDetails = XLSX.utils.json_to_sheet(detailsData);

      // Set accounting format for columns and adjust column widths
      const range = XLSX.utils.decode_range(wsDetails['!ref'] || 'A1');
      const moneyColumns = [
        19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33,
      ]; // Columns U-AH

      // Set column widths
      wsDetails['!cols'] = Array(range.e.c + 1).fill({ wch: 12 });
      moneyColumns.forEach((col) => {
        if (wsDetails['!cols']) {
          wsDetails['!cols'][col] = { wch: 15 }; // Make money columns wider
        }
      });

      // Set number format
      for (let row = range.s.r; row <= range.e.r; row++) {
        for (const col of moneyColumns) {
          const cell = XLSX.utils.encode_cell({ r: row, c: col });
          if (!wsDetails[cell]) continue;
          wsDetails[cell].z =
            '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
        }
      }
      XLSX.utils.book_append_sheet(wb, wsDetails, 'Detalle');

      // Create settlements sheet
      const settlementsData = prepareSettlementsData(allSettlements);
      const wsSettlements = XLSX.utils.json_to_sheet(settlementsData);

      // Set accounting format for column H (Liquidación FOB)
      const range2 = XLSX.utils.decode_range(wsSettlements['!ref'] || 'A1');

      // Set column widths
      wsSettlements['!cols'] = Array(range2.e.c + 1).fill({ wch: 12 });
      if (wsSettlements['!cols']) {
        wsSettlements['!cols'][8] = { wch: 15 }; // Make FOB column wider
      }

      for (let row = range2.s.r; row <= range2.e.r; row++) {
        const cell = XLSX.utils.encode_cell({ r: row, c: 8 }); // Column I (Liquidación FOB)
        if (!wsSettlements[cell]) continue;
        wsSettlements[cell].z =
          '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
      }

      XLSX.utils.book_append_sheet(wb, wsSettlements, 'Liquidaciones');

      // Export the file
      const exportFileName = `Liquidaciones_Todos_Exportadores${
        selectedSeason ? `_${selectedSeason}` : ''
      }.xlsx`;
      XLSX.writeFile(wb, exportFileName);
      toast.success(
        `${allSettlements.length} liquidaciones exportadas correctamente`
      );
    } catch (error) {
      console.error('Error al exportar liquidaciones:', error);
      toast.error('Error al exportar liquidaciones');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const bottomContent = (
    <div className='flex w-full justify-between items-center px-2 py-2'>
      <div className='flex flex-col items-center'>
        <span className='text-small text-default-400'>
          Total {totalCount} liquidaciones
        </span>
        <Select
          size='sm'
          label='Filas por página'
          className='w-40'
          selectedKeys={new Set([rowsPerPage.toString()])}
          onSelectionChange={(keys) => {
            const selected = Array.from(keys)[0];
            setRowsPerPage(Number(selected));
            setPage(1);
          }}
        >
          {[10, 20, 30, 40, 50].map((pageSize) => (
            <SelectItem key={pageSize.toString()} value={pageSize.toString()}>
              {pageSize.toString()}
            </SelectItem>
          ))}
        </Select>
      </div>
      <Pagination
        isCompact
        showControls
        showShadow
        color='primary'
        page={page}
        total={Math.ceil(totalCount / rowsPerPage)}
        onChange={(page) => setPage(page)}
      />
    </div>
  );

  return (
    <PageLayout className='relative'>
      {/* Overlay de carga cuando se está procesando una liquidación */}
      {processingSettlement && (
        <div className='fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'>
          <div className='bg-background p-6 rounded-lg shadow-xl text-center'>
            <div className='animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary mx-auto mb-4'></div>
            <h3 className='text-lg font-medium mb-2'>Procesando liquidación</h3>
            <p className='text-default-500'>
              Por favor espere mientras se procesan los datos de mercado...
            </p>
          </div>
        </div>
      )}

      <Header title='Liquidaciones' />

      <div className='flex justify-between items-center mb-6'>
        <div className='flex gap-4 items-center'>
          <Input
            placeholder='Buscar por referencia, cliente o contenedor...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            startContent={<Icon icon='tabler:search' className='text-default-300' />}
            className='w-80'
          />
          <select
            value={selectedSeason}
            onChange={(e) => setSelectedSeason(e.target.value)}
            className='px-4 py-2 rounded-lg border border-divider'
          >
            <option value=''>Todas las temporadas</option>
            {uniqueSeasons.map((season) => (
              <option key={season} value={season}>
                {season}
              </option>
            ))}
          </select>
          <Button
            color={showPendingOnly ? 'default' : 'warning'}
            variant={showPendingOnly ? 'flat' : 'bordered'}
            onPress={() => setShowPendingOnly(!showPendingOnly)}
          >
            {showPendingOnly ? 'Mostrar Todas' : 'Mostrar Solo Pendientes'}
          </Button>
        </div>
        <div className='flex gap-4 items-center'>
          <Checkbox
            isSelected={notifyViaEmail}
            onValueChange={setNotifyViaEmail}
          >
            Notificar vía email
          </Checkbox>
          <Button
            variant='flat'
            onPress={exportAllSettlements}
            isDisabled={downloadingExcel}
            startContent={<Icon icon='tabler:download' />}
          >
            {downloadingExcel
              ? 'Exportando liquidaciones...'
              : 'Exportar Liquidaciones de Todos los Exportadores'}
          </Button>
          <Button
            color='primary'
            onPress={() => setIsRecalculateModalOpen(true)}
          >
            Recalcular Mercado
          </Button>
        </div>
      </div>

      <Card>
        {loading ? (
          <LoadingState />
        ) : (
          <div className='overflow-x-auto'>
            <SettlementsTable
              settlements={settlements}
              selectedKeys={selectedKeys}
              onSelectionChange={setSelectedKeys}
              onEdit={handleEditSettlement}
              onDelete={handleDeleteSettlement}
              onApprove={handleApproveSettlement}
              bottomContent={bottomContent}
            />
          </div>
        )}
      </Card>

      <EditSettlementDialog
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        settlement={selectedSettlement}
        onEdit={fetchSettlements}
        onDelete={fetchSettlements}
      />

      <DeleteSettlementModal
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        settlement={settlementToDelete}
        onSuccess={fetchSettlements}
      />

      <RecalculateMarketModal
        isOpen={isRecalculateModalOpen}
        onClose={() => setIsRecalculateModalOpen(false)}
        onSuccess={fetchSettlements}
      />
    </PageLayout>
  );
};

export default SuperadminSettlements;
